import React from "react";
import { MdOutlineFileDownload } from "react-icons/md";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";
import autoTable from "jspdf-autotable";

import { getCurrencySymbol, renderTableTotal } from "../config/tableHelper";
import "./../OnBoardingAudit/NotoSans-Regular-normal";

function Download({
  columns,
  data,
  closeModalBoxes,
  showDownloadOptions,
  setShowDownloadOptions,
  DownloadRef,
  showTotal,
}) {
  const downloadData = (format) => {
    const visibleColumns = columns.filter((col) => col.visible);
    const headers = visibleColumns.map((col) => col.title);

    // ✅ Helper to strip HTML tags
    const stripHtml = (html) => {
      if (!html || typeof html !== "string") return html;
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html.replace(/<br\s*\/?>/gi, "\n");
      return tempDiv.textContent || tempDiv.innerText || "";
    };

    const formatForDownload = (value, col) => {
      if (value === null || value === undefined) return "";

      let actualValue = value;
      let percentValue = undefined;

      // ✅ Check for Compare Object ({ value: 123, percent: 10 })
      if (
        typeof value === "object" &&
        value !== null &&
        value.value !== undefined
      ) {
        actualValue = value.value;
        percentValue = value.percent; // Extract percent if available
      }

      const formatNum = (num) => {
        return new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(num);
      };

      const currency =
        typeof getCurrencySymbol === "function" ? getCurrencySymbol() : "$";

      let formattedValue = "";

      // Format the main value based on column type
      switch (col.type) {
        case "currency":
          formattedValue = `${currency}${formatNum(actualValue)}`;
          break;
        case "float":
        case "percentage":
          formattedValue = `${Number(actualValue).toFixed(2)}%`;
          break;
        case "number":
          formattedValue = formatNum(actualValue);
          break;
        case "string":
        default:
          if (Array.isArray(actualValue)) {
            formattedValue = actualValue
              .map((v) => v.label_name || v)
              .join(", ");
          } else if (
            typeof actualValue === "string" &&
            /<[a-z][\s\S]*>/i.test(actualValue)
          ) {
            formattedValue = stripHtml(actualValue);
          } else {
            formattedValue = actualValue;
          }
          break;
      }

      // ✅ Append Percentage if it exists (Compare Data)
      if (percentValue !== undefined) {
        const sign = percentValue > 0 ? "+" : "";
        const formattedPercent = `${sign}${Number(percentValue).toFixed(1)}%`;
        // Use newline to stack them in Excel cells or PDF rows
        formattedValue = `${formattedValue}\n(${formattedPercent})`;
      }

      return formattedValue;
    };

    const rows = [];

    data.forEach((item) => {
      // 1. Add Parent Row
      rows.push(
        visibleColumns.map((col) => formatForDownload(item[col.key], col))
      );

      // 2. Add Segment Rows (if they exist)
      if (
        item.segments &&
        Array.isArray(item.segments) &&
        item.segments.length > 0
      ) {
        item.segments.forEach((segment) => {
          rows.push(
            visibleColumns.map((col) =>
              formatForDownload(segment[col.key], col)
            )
          );
        });
      }
    });

    if (showTotal) {
      const totalRow = visibleColumns.map((col, i) => {
        if (i === 0) return "Total";
        // Calculate total using the helper function
        const calculatedTotal = renderTableTotal(col, data);
        return calculatedTotal ?? "";
      });
      rows.push(totalRow);
    }

    // ✅ Step 3: Export to selected format
    if (format === "pdf") {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "A4",
      });
      autoTable(doc, {
        head: [headers],
        body: rows,
        styles: {
          font: "NotoSans-Regular",
          fontStyle: "normal",
          fontSize: 8,
          cellPadding: 4,
          valign: "middle", // Align text vertically
        },
        headStyles: {
          fillColor: [40, 53, 147],
          textColor: 255,
          fontSize: 9,
        },
        columnStyles: {
          0: { cellWidth: 120 },
        },
        didDrawPage: (data) => {
          doc.setFontSize(12);
          doc.setTextColor(40);
        },
        startY: 50,
        theme: "striped",
        didParseCell: function (data) {
          if (showTotal && data.row.index === rows.length - 1) {
            data.cell.styles.fontStyle = "bold";
          }
        },
      });
      doc.save("data.pdf");
    } else if (format === "csv" || format === "excel") {
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      if (format === "csv") {
        XLSX.writeFile(wb, "data.csv");
      } else {
        XLSX.writeFile(wb, "data.xlsx");
      }
    } else if (format === "xml") {
      const xmlContent = `
            <root>
              ${rows
                .map(
                  (row) => `
                <row>
                  ${visibleColumns
                    .map((col, i) => `<${col.key}>${row[i] || ""}</${col.key}>`)
                    .join("")}
                </row>
              `
                )
                .join("")}
            </root>
          `;
      const blob = new Blob([xmlContent], { type: "application/xml" });
      FileSaver.saveAs(blob, "data.xml");
    } else if (format === "google_sheets") {
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = `https://docs.google.com/spreadsheets/d/your-sheet-id/edit?usp=sharing`;
      window.open(url, "_blank");
      FileSaver.saveAs(blob, "data.csv");
    }

    setShowDownloadOptions(false);
  };

  return (
    <div className="relative" ref={DownloadRef}>
      <button
        className="bg-transparent text-gray-600 px-4 py-2 rounded items-center hover:bg-slate-100"
        onClick={() => {
          closeModalBoxes("Download");
        }}
      >
        <MdOutlineFileDownload className="ml-5 " />
        Download
      </button>
      {showDownloadOptions && (
        <div className="absolute right-0 bg-white shadow-md rounded p-4 mt-2 z-40 border border-gray-200">
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => downloadData("pdf")}
          >
            PDF
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => downloadData("csv")}
          >
            CSV
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => downloadData("excel")}
          >
            Excel
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => downloadData("xml")}
          >
            XML
          </button>
        </div>
      )}
    </div>
  );
}

export default Download;
